import { oktaAuth } from "../../auth/services/oktaAuth";
import { api, apiUrl } from "../../shared/services/service";
import axios from "axios";

let cachedToken: string;

api.interceptors.request.use((config) => {
  const token = oktaAuth.getAccessToken() || cachedToken;
  if (token) {
    cachedToken = token;
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn("No access token for request:", config.url);
  }
  return config;
});

const handleApiError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    console.error("Unknown error:", error);
    alert("An unexpected error occurred while downloading the BQ document.");
    return;
  }

  const { response, request, message } = error;
  if (response) {
    console.error("API Error:", response.data, "Status:", response.status);
    alert(`Download failed: ${response.data?.error || "Server error"}`);
  } else if (request) {
    console.error("No response received:", request);
    alert("No response from server. Please check your connection.");
  } else {
    console.error("Request setup error:", message);
    alert(`Request error: ${message}`);
  }
};

export const downloadBQ = async (
  bqPayload: Record<string, any>,
  setProgress?: (completion: number) => void,
): Promise<void> => {
  try {
    const response = await api.post(
      `${apiUrl}/downloadBQ`,
      { bq: bqPayload },
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
        onDownloadProgress: ({ loaded, total }) => {
          setProgress?.(Math.floor((loaded / (total ?? 1)) * 100));
        },
      },
    );

    if (!response || !response.data || response.data.size === 0) {
      throw new Error("Empty or invalid response from server");
    }

    // ✅ Extract vesselName and imoNumber from payload
    const vesselName = bqPayload?.vesselName ?? "Vessel";
    const imoNumber = bqPayload?.imoNumber ?? "UnknownIMO";

    // ✅ Create a blob from the response
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    // ✅ Create a link element and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const safeVesselName = vesselName.replace(/[^a-zA-Z0-9-_]/g, "_");
    const safeImoNumber = imoNumber.replace(/[^a-zA-Z0-9-_]/g, "_");
    link.download = `BQ_${safeVesselName}_${safeImoNumber}.docx`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleApiError(error);
    console.error("Failed to download BQ document:", error);
  }
};
